import type { Appointment } from 'types';
import type { CheckinData } from 'pages/Appointments/AppointmentsDashboard/types';

// Action Types
export const SET_CHECKIN_DATA = 'SET_CHECKIN_DATA' as const;
export const CLEAR_CHECKIN_DATA = 'CLEAR_CHECKIN_DATA' as const;
export const SET_CHECKIN_MODAL_OPEN_STATE = 'SET_CHECKIN_MODAL_OPEN_STATE' as const;
export const SET_CHECKIN_TEAM = 'SET_CHECKIN_TEAM' as const;
export const CLEAR_CHECKIN_TEAM = 'CLEAR_CHECKIN_TEAM' as const;
export const SET_APPOINTMENTS_FOR_DAY = 'SET_APPOINTMENTS_FOR_DAY' as const;
export const CLEAR_APPOINTMENTS_FOR_DAY = 'CLEAR_APPOINTMENTS_FOR_DAY' as const;
// Action Types
type SetCheckinDataAction = {
  type: typeof SET_CHECKIN_DATA;
  payload: CheckinData;
};

type ClearCheckinDataAction = {
  type: typeof CLEAR_CHECKIN_DATA;
};

type SetCheckinModalOpenStateAction = {
  type: typeof SET_CHECKIN_MODAL_OPEN_STATE;
  payload: boolean;
};

type SetAppointmentsForDayAction = {
  type: typeof SET_APPOINTMENTS_FOR_DAY;
  payload: Appointment[];
};

type ClearAppointmentsForDayAction = {
  type: typeof CLEAR_APPOINTMENTS_FOR_DAY;
};

// ISA-2402 story 2.3 review round 1 [HIGH] fix: the pick is keyed by the repair order it was
// made for -- mirroring state.quoting.skillOverridesByRecommendationId's per-recommendation
// keying -- so a pick abandoned on one repair order is never read as the current selection on a
// different one. See AppointmentsState.checkInTeam below for the read-side contract.
type CheckInTeamSelection = {
  repairOrderId: number;
  team: string | null;
};

type SetCheckinTeamAction = {
  type: typeof SET_CHECKIN_TEAM;
  payload: CheckInTeamSelection;
};

type ClearCheckinTeamAction = {
  type: typeof CLEAR_CHECKIN_TEAM;
};

export type AppointmentsActionTypes =
  | SetCheckinDataAction
  | ClearCheckinDataAction
  | SetCheckinModalOpenStateAction
  | SetAppointmentsForDayAction
  | ClearAppointmentsForDayAction
  | SetCheckinTeamAction
  | ClearCheckinTeamAction;

// State type
export type AppointmentsState = {
  checkinData: CheckinData | null;
  isCheckInModalOpen: boolean;
  appointmentsForDay: Appointment[];
  // ISA-2402 story 2.3 (AD-3): the Team the advisor picked at check-in. Store state, not a
  // screen-level useState, because the Save button navigates to the document-preparation route
  // BEFORE useSignableDocumentPreparation issues the activate() request that carries it — only
  // store state survives that navigation. Deliberately NOT part of CheckinFormState: the Team
  // must never ride the check-in autosave PUT, which flushes before it considers a DMS push.
  // Keyed by repairOrderId (review round 1 [HIGH] fix): a pick is only ever the "current"
  // selection for the repair order it was made on, so a different repair order's check-in page
  // computes its own selector read as null rather than inheriting a stale pick -- ownership of
  // the CROSS-repair-order case is structural, not dependent on remembering to dispatch a clear
  // at unmount or route change. It does NOT cover sign-out on the SAME repair order (a later
  // advisor could otherwise see an earlier one's abandoned pick) -- that path is closed
  // separately by an explicit clearCheckinTeam() dispatch from signout()
  // (redux/actions/auth/index.ts), not by this keying.
  checkInTeam: CheckInTeamSelection | null;
};
